import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { SortableHeader, type SortDir } from '../KpiDetailModal/SortableHeader.js';
import { compareTableValues } from '../../../lib/table-sort.js';
import { useKpiBugEscapeTimingDetails } from '../../../hooks/useKpi.js';
import type { BugEscapeTimingDetail, EscapeTimingBin, KpiTeam } from '../../../types/api.js';

/** ソート可能な列キー @graph-connects none */
type EscapeSortKey = keyof Pick<
  BugEscapeTimingDetail,
  | 'issueKey'
  | 'summary'
  | 'daysToDiscovery'
  | 'causeProjectReleasedDate'
  | 'createdDate'
  | 'incidentLevel'
  | 'assignee'
>;

export interface EscapeTimingDetailModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 対象ビン。未選択時は null */
  bin: EscapeTimingBin | null;
  /** 取得月数（チャートと同じ期間で絞り込む） */
  months: number;
  /** チーム絞り込み（チャートと同じ絞り込みで一覧を出す）。undefined / all は全体 */
  team?: KpiTeam;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 信頼性ページのエスケープ・タイミングチャートのビンクリック→ドリルダウンモーダル。指定ビンの個別バグ課題を経過日数の大きい順に一覧表示し、Backlogリンク・起因案件リリース日（窓判定用）・起票日・日数を確認できる
 * @graph-connects useKpiBugEscapeTimingDetails [calls] 指定ビンのバグ個別課題一覧を取得
 * @graph-connects Modal [calls] 汎用モーダルで内容を表示
 */
export function EscapeTimingDetailModal({
  open,
  onClose,
  bin,
  months,
  team,
}: EscapeTimingDetailModalProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<EscapeSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const params = useMemo(() => {
    if (!open || bin === null) return null;
    // 全体表示（undefined / all）のときは team を付けず、チャート側と同じクエリキーに揃える
    return team !== undefined && team !== 'all' ? { bin, months, team } : { bin, months };
  }, [open, bin, months, team]);

  const { data, isLoading } = useKpiBugEscapeTimingDetails(params);

  // 未ソート時は API の既定順（経過日数の大きい順）のまま表示する
  const bugs = useMemo<BugEscapeTimingDetail[]>(() => {
    const rows = data?.data ?? [];
    if (sortKey === null) return rows;
    return [...rows].sort((a, b) => compareTableValues(a[sortKey], b[sortKey], sortDir));
  }, [data, sortKey, sortDir]);

  const handleSort = (key: EscapeSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const binLabel = bin !== null ? t(`reliability.escapeBin.${bin}`) : '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={t('reliability.escapeDetail.title', { bin: binLabel })}
      description={t('reliability.escapeDetail.description', { count: bugs.length })}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
        </div>
      ) : bugs.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          {t('reliability.escapeDetail.noItems')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[150px]">
                  <SortableHeader
                    label={t('reliability.escapeDetail.issueKey')}
                    sortKey="issueKey"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] min-w-[280px]">
                  <SortableHeader
                    label={t('reliability.escapeDetail.summary')}
                    sortKey="summary"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[110px]">
                  <div className="flex justify-end">
                    <SortableHeader
                      label={t('reliability.escapeDetail.daysToDiscovery')}
                      sortKey="daysToDiscovery"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                  </div>
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[130px]">
                  <SortableHeader
                    label={t('reliability.escapeDetail.releasedDate')}
                    sortKey="causeProjectReleasedDate"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[130px]">
                  <SortableHeader
                    label={t('reliability.escapeDetail.createdDate')}
                    sortKey="createdDate"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[150px]">
                  <SortableHeader
                    label={t('reliability.escapeDetail.incidentLevel')}
                    sortKey="incidentLevel"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[120px]">
                  <SortableHeader
                    label={t('reliability.escapeDetail.assignee')}
                    sortKey="assignee"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {bugs.map((bug) => (
                <tr
                  key={bug.issueKey}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="py-2 px-2 whitespace-nowrap">
                    <a
                      href={`https://air-closet.backlog.jp/view/${bug.issueKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-accent-primary)] hover:underline"
                    >
                      {bug.issueKey}
                    </a>
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-primary)]">
                    <div className="line-clamp-2">{bug.summary || '-'}</div>
                  </td>
                  <td className="py-2 px-2 text-right text-[var(--color-text-primary)] whitespace-nowrap tabular-nums">
                    {t('reliability.escapeDetail.daysUnit', { count: bug.daysToDiscovery })}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.causeProjectReleasedDate}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.createdDate}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.incidentLevel ?? '-'}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.assignee ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
